// Pure, deterministic connection rules for the flowchart editor.
//
// These run BOTH while a connection is being dragged (via React Flow's
// `isValidConnection`, so an invalid drop simply won't connect — no error
// popups, no half-made edges) and as a final guard in `onConnect`. Keeping the
// logic here (no React, no side effects) means it is unit-testable and the UI
// and tests share exactly one source of truth.
//
// Backend contract this mirrors (skeptics-backend):
//   - one connector per ordered (source -> target) pair
//     (unique index `flowchart_connectors_unique_edge`)
//   - a node cannot connect to itself

export interface FlowConnectionLike {
  source?: string | null;
  target?: string | null;
  sourceHandle?: string | null;
  targetHandle?: string | null;
}

export interface FlowEdgeLike {
  source: string;
  target: string;
}

export type ConnectionRejectionReason = 'incomplete' | 'self' | 'duplicate';

/**
 * Returns why a connection is not allowed, or `null` if it is allowed.
 * `existingEdges` is the current edge list (order: source -> target matters).
 */
export function getConnectionRejection(
  connection: FlowConnectionLike | null | undefined,
  existingEdges: FlowEdgeLike[] | null | undefined,
): ConnectionRejectionReason | null {
  const source = connection?.source ?? null;
  const target = connection?.target ?? null;

  // React Flow fires intermediate callbacks with a missing endpoint while the
  // user is still dragging — that's not an error, just "not connectable yet".
  if (!source || !target) return 'incomplete';

  if (source === target) return 'self';

  const edges = existingEdges ?? [];
  const alreadyConnected = edges.some(
    (edge) => edge.source === source && edge.target === target,
  );
  if (alreadyConnected) return 'duplicate';

  return null;
}

/** True when a connection may be created. */
export function isConnectionAllowed(
  connection: FlowConnectionLike | null | undefined,
  existingEdges: FlowEdgeLike[] | null | undefined,
): boolean {
  return getConnectionRejection(connection, existingEdges) === null;
}

/** Human-readable message for a rejection reason (UI/toast use). */
export function connectionRejectionMessage(
  reason: ConnectionRejectionReason,
): string {
  switch (reason) {
    case 'self':
      return 'A step cannot connect to itself.';
    case 'duplicate':
      return 'These two steps are already connected.';
    case 'incomplete':
    default:
      return '';
  }
}
